import { useState } from "react";

import Link from "next/link";
import { useRouter } from "next/router";

import { EditOutlined, QuestionCircleOutlined } from "@ant-design/icons";
import { Trans } from "@lingui/macro";
import { Button, Col, Popover, Row, Space, Typography } from "antd";
import { replace } from "lodash";
import styled from "styled-components";
import useSWR from "swr";

import { LoadingIndicator } from "components/loading";

import { useApplicationContext } from "contexts/app";

import ArrowRightIcon from "icons/arrow-right";
import DriverIcon from "icons/permissions/driver";
import DriverGroupIcon from "icons/permissions/driver-group";
import ReportIcon from "icons/permissions/report-request";
import UserIcon from "icons/permissions/user";
import UserGroupIcon from "icons/permissions/user-group";
import VehicleIcon from "icons/permissions/vehicle";
import VehicleGroupIcon from "icons/permissions/vehicle-group";

const hiddenObjectPermission = ["account"];

const Container = styled.div`
  width: 500px;
  max-height: 600px;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 2px 4px;
`;

export const LoaderContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 50px;
  width: 500px;
`;

const StyledQuestionIcon = styled(QuestionCircleOutlined)`
  color: #000000;

  :hover {
    cursor: pointer;
  }
`;

export const getIcon = (type: string) => {
  switch (type) {
    case "vehicle":
      return <VehicleIcon />;
    case "vehicle_group":
      return <VehicleGroupIcon />;
    case "report_request":
      return <ReportIcon />;
    case "user":
      return <UserIcon />;
    case "driver":
      return <DriverIcon />;
    case "driver_group":
      return <DriverGroupIcon />;
    case "account":
    case "user_group":
      return <UserGroupIcon color={"black"} />;
    default:
      return <div />;
  }
};

interface ContentProps {
  data: { title: string; link: string; disabled: boolean }[];
  loading: boolean;
}

const Content = ({ data, loading }: ContentProps) => {
  const router = useRouter();

  if (loading) {
    return (
      <LoaderContainer>
        <LoadingIndicator />
      </LoaderContainer>
    );
  }

  return (
    <Container>
      <Typography.Text>
        <Trans>This permission comes from multiple sources:</Trans>
      </Typography.Text>
      <Row gutter={[0, 8]}>
        {data?.map((row, index) => {
          const hideButton = row.link === router.asPath;

          return (
            <Col span={24} key={index}>
              <Row justify={"space-between"} align={"middle"} gutter={8}>
                <Col span={20}>{row.title}</Col>
                <Col span={4}>
                  <Row justify={"end"}>
                    <Link href={row.link}>
                      <Button
                        size={"small"}
                        type={"default"}
                        icon={<EditOutlined />}
                        disabled={row.disabled || hideButton}
                      >
                        <Trans>EDIT</Trans>
                      </Button>
                    </Link>
                  </Row>
                </Col>
              </Row>
            </Col>
          );
        })}
      </Row>
    </Container>
  );
};

export interface PermissionDetails {
  objectType: string;
  objectValue: string;
  detailsEndpoint: string;
  permissionKey: string;
}

interface EditPermissionLinkProps {
  permission: string;
  permissionValue: string;
  permissionDetails: PermissionDetails;
}

const EditPermissionLink = ({ permissionDetails, permission, permissionValue }: EditPermissionLinkProps) => {
  const { httpClient } = useApplicationContext();
  const { objectType, objectValue, detailsEndpoint, permissionKey } = permissionDetails;
  const [loadInheritance, setLoadInheritance] = useState(false);
  const router = useRouter();

  const { data, error } = useSWR(
    loadInheritance &&
      `${objectType}/${objectValue}/${detailsEndpoint}/?permission=${permission}&${permissionKey}=${permissionValue}`
  );

  const isLoading = data === undefined && error === undefined && loadInheritance;

  const content = data?.map((row: any) => {
    if (row) {
      let link = "";
      const objType = replace(row.object_type, "_", "-");
      const urlPath = replace(replace(row[row.subject_type], httpClient?.basePath || "", ""), "_", "-");
      const hideObjectPermission = !hiddenObjectPermission.includes(row.object_type);

      if (urlPath) {
        link = `/settings/${urlPath}?tab=${objType}-permissions${router?.query?.permission ? "&permission=true" : ""}`;
      }

      return {
        title: (
          <Space align={"center"}>
            <Row justify="center" align="middle">
              {getIcon(row.subject_type)}
            </Row>
            <Typography.Text
              strong
              ellipsis={{
                tooltip: row.subject_name,
              }}
            >
              {row.subject_name}
            </Typography.Text>
            {hideObjectPermission && (
              <>
                <Row justify={"center"} align={"middle"}>
                  <ArrowRightIcon />
                </Row>
                <Row justify="center" align="middle">
                  {getIcon(row.object_type)}
                </Row>
                <Typography.Text
                  strong
                  ellipsis={{
                    tooltip: row.object_name,
                  }}
                >
                  {row.object_name}
                </Typography.Text>
              </>
            )}
          </Space>
        ),
        disabled: row.read_only,
        link: link,
      };
    }
  });

  return (
    <Popover
      content={<Content data={content} loading={isLoading} />}
      trigger={["click"]}
      onOpenChange={(v) => {
        if (!v) {
          setLoadInheritance(v);
          return;
        }
        setLoadInheritance(v);
      }}
      mouseLeaveDelay={0.5}
      mouseEnterDelay={0.7}
    >
      <StyledQuestionIcon />
    </Popover>
  );
};

export default EditPermissionLink;
